<template>
  <Dialog
    :visible="visible"
    :header="t('templateBuilder.conditionalDisplay.modalTitle')"
    :modal="true"
    :style="{ width: '90vw', maxWidth: '1200px' }"
    :closable="true"
    @update:visible="handleClose"
  >
    <div class="conditional-display-modal-content">
      <p class="mb-3 text-left">
        {{ t('templateBuilder.conditionalDisplay.description') }}
      </p>

      <SegmentationsPanel
        v-if="visible && localConfig !== null"
        v-model:model-value="localConfig"
        origin="conditional"
        :theme="detectedTheme"
        :collection-contexts="collectionContexts"
        :show-prompt-a-i="false"
        @update:model-value="handleConfigUpdate"
      />
    </div>

    <template #footer>
      <Button
        :label="t('cancel')"
        icon="far fa-times"
        class="p-button-secondary"
        @click="handleCancel"
      />
      <Button
        v-if="hasExistingConditions"
        :label="t('common.delete')"
        icon="far fa-trash-alt"
        class="p-button-danger"
        @click="handleDelete"
      />
      <Button
        :label="t('validate')"
        icon="far fa-check"
        @click="handleValidate"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, watch, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import { CollectionContext, getCollectionSchema } from '@/composables/template-builder/collectionContext';

export default defineComponent({
  name: 'ConditionalDisplayModal',

  components: {
    Dialog,
    Button,
    SegmentationsPanel,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    modelValue: {
      type: Array,
      default: () => [[{}]],
    },

    // Optional: HTML element to detect context (e.g., product loop)
    contextElement: {
      type: Object as () => HTMLElement | null,
      default: null,
    },
  },

  emits: ['update:visible', 'update:modelValue', 'validate', 'delete'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const localConfig = ref(props.modelValue);

    // Detect theme and collection contexts from context element
    const detectedTheme = ref<string | string[]>('');
    const collectionContexts = ref<CollectionContext[]>([]);

    /**
     * Detect all parent collection contexts and determine theme
     * Walks up the DOM tree to find all elements with s-collection attribute
     */
    const detectContextsFromElement = async (element: HTMLElement | null): Promise<{
      theme: string | string[];
      contexts: CollectionContext[];
    }> => {
      if (!element) return { theme: '', contexts: [] };

      const collectionsFound: { collectionName: string; iterator: string }[] = [];
      let hasProductsCollection = false;

      // Walk up DOM to find s-collection attributes (set by setLoopConfiguration)
      let current: HTMLElement | null = element;
      while (current) {
        const collectionAttr = current.getAttribute('s-collection');
        const itemAttr = current.getAttribute('s-item');

        if (collectionAttr && itemAttr) {
          let collectionName = collectionAttr;

          // Try to parse as JSON (Code.vue stores JSON in s-collection)
          try {
            const config = JSON.parse(collectionAttr);
            collectionName = config.name || config.collection || collectionAttr;
          } catch {
            // Keep as plain string
          }

          if (collectionName === 'products') {
            hasProductsCollection = true;
          }

          // Avoid duplicates
          if (!collectionsFound.some((c) => c.collectionName === collectionName && c.iterator === itemAttr)) {
            collectionsFound.push({ collectionName, iterator: itemAttr });
          }
        }

        current = current.parentElement;
      }

      // Load schemas in parallel
      const contexts: CollectionContext[] = await Promise.all(
        collectionsFound.map(async ({ collectionName, iterator }) => {
          const { schema } = await getCollectionSchema(collectionName);
          return {
            collection: collectionName,
            iterator,
            schema,
          };
        }),
      );

      // Determine theme based on collections found
      const theme = hasProductsCollection ? ['contacts', 'products'] : 'contacts';

      return { theme, contexts };
    };

    // Watch for contextElement changes
    watch(() => props.contextElement, async (newElement) => {
      const result = await detectContextsFromElement(newElement);
      detectedTheme.value = result.theme;
      collectionContexts.value = result.contexts;
    }, { immediate: true });

    // Sync with prop changes
    watch(() => props.modelValue, (newVal) => {
      localConfig.value = newVal;
    });

    const handleConfigUpdate = (newConfig: any) => {
      localConfig.value = newConfig;
      emit('update:modelValue', newConfig);
    };

    const handleClose = (value: boolean) => {
      emit('update:visible', value);
    };

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const handleValidate = () => {
      const cleanedConfig = (localConfig.value as any[][])
        .map((blockAND: any[]) => blockAND.filter((blockOR: any) => Object.keys(blockOR).length > 0))
        .filter((blockAND: any[]) => blockAND.length > 0);

      emit('validate', cleanedConfig);
      emit('update:visible', false);
    };

    const handleDelete = () => {
      emit('delete');
      emit('update:visible', false);
    };

    // Check if there are existing conditions
    const hasExistingConditions = computed(() => {
      if (!localConfig.value || !Array.isArray(localConfig.value)) return false;

      return (localConfig.value as any[][]).some((blockAND: any[]) => blockAND.some((blockOR: any) => Object.keys(blockOR).length > 0));
    });

    return {
      t,
      localConfig,
      detectedTheme,
      collectionContexts,
      handleConfigUpdate,
      handleClose,
      handleCancel,
      handleValidate,
      handleDelete,
      hasExistingConditions,
    };
  },
});
</script>

<style scoped>
.conditional-display-modal-content {
  min-height: 400px;
}
</style>
